import { useState } from 'react'
import { Outlet } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Alert } from '@factorialco/f0-react'
import { OneDataCollection, SectionHeader } from '@factorialco/f0-react/dist/experimental'
import { Add } from '@factorialco/f0-react/icons/app'
import {
  OneOnOnesListDocument,
  OneOnOnesVisibilityPolicyDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import useSearchParams from 'modules/core/hooks/useSearchParams'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { isManager } from 'modules/employees/lib/isManager'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import NewMeetingModal from 'modules/one_on_ones/components/NewMeetingModal'
import { useDownloadOneOnOnesData } from 'modules/one_on_ones/hooks/useDownloadOneOnOnesData'

import TeamOneOnOneList from './TeamOneOnOneList'

interface Params {
  creatingNewOneOnOne: string
}
function OneOnOnesListPage() {
  const {
    employee,
    access: { locale },
  } = useCurrentSession()

  const isUserManager = employee ? isManager(employee) : false

  const { data: policyData } = useQuery(OneOnOnesVisibilityPolicyDocument)
  const hasFullVisibility = policyData?.oneOnOnes?._policies?.fullVisibility?.can ?? false

  const { navigation } = useNavigation()
  const searchParams = useSearchParams<Params>()
  const [showNewModal, setNewAddModal] = useState(Boolean(searchParams?.creatingNewOneOnOne))
  const { downloadData } = useDownloadOneOnOnesData()
  const source = useGraphqlDataCollectionSource({
    document: OneOnOnesListDocument,
    mapData: (data) => {
      return data.oneOnOnes.oneOnOnesConnection.nodes
    },
    mapPagination: (data) => ({
      total: Number(data.oneOnOnes.oneOnOnesConnection.totalCount),
    }),
    search: {
      enabled: true,
    },
    mapVariables: ({ search }) => ({
      search,
      participantIds: [String(employee?.id)],
    }),
    itemUrl(item) {
      return navigation.performanceOneOnOnes.oneOnOne.meetings.fullPath({
        oneOnOneId: item.id,
      })
    },
    primaryActions: () => ({
      label: i18n.t('one_on_ones.new.action'),
      icon: Add,
      onClick: () => setNewAddModal(true),
    }),
  })

  const canSeeTeamOneOnOnes = isUserManager || hasFullVisibility
  const formatMeetingDate = (date?: string | null) => {
    if (!date) return '-'

    return moment(date).locale(locale).format('LL')
  }

  return (
    <ResponsivePage.Body>
      {showNewModal && (
        <NewMeetingModal
          onClose={() => setNewAddModal(false)}
          refetchQueries={[OneOnOnesListDocument]}
        />
      )}
      <F0Alert
        action={{
          label: i18n.t('one_on_ones.deprecation_banner.download_data'),
          onClick: downloadData,
        }}
        description={i18n.t('one_on_ones.deprecation_banner.description')}
        title={i18n.t('one_on_ones.deprecation_banner.title')}
        variant="warning"
      />

      {canSeeTeamOneOnOnes && (
        <>
          <SectionHeader
            title={i18n.t('one_on_ones.list_view.title')}
            description={i18n.t('one_on_ones.list_view.description')}
          />
          <Box paddingTop="s24" />
        </>
      )}

      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('one_on_ones.table.participant'),
                  render: (item) => {
                    const participant =
                      item.firstParticipant?.id === employee?.id
                        ? item.secondParticipant
                        : item.firstParticipant

                    return {
                      type: 'person',
                      value: {
                        firstName: participant?.firstName ?? '',
                        lastName: participant?.lastName ?? '',
                        src: buildUrl(participant?.avatar?.url),
                      },
                    }
                  },
                },
                {
                  label: i18n.t('one_on_ones.table.last_meeting'),
                  render: (item) => ({
                    type: 'text',
                    value: formatMeetingDate(item.lastMeeting?.startTime),
                  }),
                },
                {
                  label: i18n.t('one_on_ones.table.next_meeting'),
                  render: (item) => ({
                    type: 'text',
                    value: formatMeetingDate(item.upcomingMeeting?.startTime),
                  }),
                },
              ],
            },
          },
        ]}
      />

      {canSeeTeamOneOnOnes && (
        <>
          <Box paddingTop="s24" />
          <TeamOneOnOneList />
        </>
      )}

      <Outlet />
    </ResponsivePage.Body>
  )
}

export default OneOnOnesListPage
